'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: Gtag.Gtag;
    dataLayer?: any[];
  }
}

// Analytics event types
export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

// User behavior tracking
export interface UserBehaviorEvent {
  event_name: string;
  country: string;
  category?: string;
  platform?: string;
  article_id?: string;
  engagement_time?: number;
}

// Geography-based tracking
export interface GeographyEvent {
  country_code: string;
  country_name: string;
  trending_topic?: string;
  article_count?: number;
}

// Platform engagement tracking
export interface PlatformEngagementEvent {
  platform: string;
  engagement_type: 'view' | 'click' | 'share' | 'read';
  article_id?: string;
  duration?: number;
}

// Category tracking
export interface CategoryEvent {
  category: string;
  country: string;
  read_count?: number;
  engagement_score?: number;
}

/**
 * Initialize Google Analytics
 */
export function initializeGA() {
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  
  if (!measurementId || measurementId === 'your-google-analytics-id-here') {
    console.warn('Google Analytics Measurement ID not configured');
    return false;
  }

  if (typeof window === 'undefined') return false;

  if (!window.dataLayer) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = [];
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false, // We'll handle page views manually
    });
  }

  return true;
}

/**
 * Hook for tracking page views
 */
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const isInitialized = initializeGA();
    if (!isInitialized) return;

    const url = pathname + (searchParams?.toString() ? `?${searchParams}` : '');
    trackPageView(url);
  }, [pathname, searchParams]);
}

/**
 * Track page views
 */
export function trackPageView(url: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: document.title,
    });
  }
}

/**
 * Track generic events
 */
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track user behavior (article views, clicks, interactions)
 */
export function trackUserBehavior(event: UserBehaviorEvent) {
  trackEvent('user_behavior', {
    event_name: event.event_name,
    country: event.country,
    category: event.category || 'uncategorized',
    platform: event.platform || 'web',
    article_id: event.article_id,
    engagement_time: event.engagement_time,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track trending topics by geography
 */
export function trackTrendingTopic(event: GeographyEvent) {
  trackEvent('trending_topic', {
    country_code: event.country_code,
    country_name: event.country_name,
    trending_topic: event.trending_topic,
    article_count: event.article_count,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track most-read categories
 */
export function trackCategoryEngagement(event: CategoryEvent) {
  trackEvent('category_engagement', {
    category: event.category,
    country: event.country,
    read_count: event.read_count,
    engagement_score: event.engagement_score,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track platform-specific engagement (Facebook, Instagram, X, YouTube, etc.)
 */
export function trackPlatformEngagement(event: PlatformEngagementEvent) {
  trackEvent('platform_engagement', {
    platform: event.platform,
    engagement_type: event.engagement_type,
    article_id: event.article_id,
    duration: event.duration,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track article read completion
 */
export function trackArticleRead(articleId: string, country: string, category: string, readTime: number) {
  trackUserBehavior({
    event_name: 'article_read',
    country,
    category,
    article_id: articleId,
    engagement_time: readTime,
  });
}

/**
 * Track country switch
 */
export function trackCountrySwitch(fromCountry: string, toCountry: string) {
  trackEvent('country_switch', {
    from_country: fromCountry,
    to_country: toCountry,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track category filter usage
 */
export function trackCategoryFilter(category: string, country: string) {
  trackEvent('category_filter', {
    category,
    country,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track social platform filter usage
 */
export function trackPlatformFilter(platform: string, country: string) {
  trackEvent('platform_filter', {
    platform,
    country,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track hot topic click
 */
export function trackHotTopicClick(topicId: string, topicTitle: string, country: string) {
  trackEvent('hot_topic_click', {
    topic_id: topicId,
    topic_title: topicTitle,
    country,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track video play
 */
export function trackVideoPlay(articleId: string, country: string, platform: string) {
  trackPlatformEngagement({
    platform,
    engagement_type: 'view',
    article_id: articleId,
  });
  
  trackUserBehavior({
    event_name: 'video_play',
    country,
    platform,
    article_id: articleId,
  });
}

/**
 * Track news card click
 */
export function trackNewsCardClick(articleId: string, country: string, category: string, platform?: string) {
  trackUserBehavior({
    event_name: 'news_card_click',
    country,
    category,
    platform,
    article_id: articleId,
  });
}

/**
 * Track source link click
 */
export function trackSourceClick(articleId: string, sourceName: string, country: string) {
  trackEvent('source_click', {
    article_id: articleId,
    source_name: sourceName,
    country,
    timestamp: new Date().toISOString(),
  });
}
function trackNewsClick(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: trackNewsClick is not implemented yet.', args);
  return null;
}

export { trackNewsClick };